import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";

const HERO_IMAGE = "/photos/photo4.jpg";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // Subtle mouse-move 3D parallax
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onMove = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      const rx = ((e.clientY / h) - 0.5) * -4;
      const ry = ((e.clientX / w) - 0.5) * 4;
      el.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(0)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[100svh] pt-[130px] md:pt-[150px] pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Overline */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <div className="flex items-center gap-3 mono-cap text-[var(--jb-navy)]">
          <span className="w-6 h-px bg-[var(--jb-rust)]" />
          Belfast · Northern Ireland · UK
        </div>
        <div className="hidden md:flex items-center gap-3 mono-cap text-[var(--jb-muted)]">
          <span>Est. 2026</span>
          <span className="w-1 h-1 rounded-full bg-[var(--jb-rust)]" />
          <span>Fully Insured · Trusted</span>
        </div>
      </div>

      {/* Headline */}
      <motion.div
        style={{ y: textY }}
        className="max-w-[1440px] mx-auto px-6 md:px-10 mt-10 md:mt-14"
      >
        <h1
          className="serif text-[var(--jb-navy)] leading-[0.92] tracking-tight text-[clamp(48px,10vw,168px)] font-normal"
          data-testid="hero-headline"
        >
          {["Safe, Efficient", "& Stress-Free", "Moving Across", ""].map((line, i) => (
            <span key={i} className="reveal-line">
              <motion.span
                variants={lineVariant}
                initial="hidden"
                animate="show"
                custom={i}
              >
                {line || <span>Belfast <span className="italic text-[var(--jb-rust)]">& Beyond.</span></span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 md:mt-12 grid md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 text-[17px] md:text-[19px] leading-[1.55] text-[var(--jb-muted)] max-w-[520px]"
            data-testid="hero-subheadline"
          >
            Professional home moves, office removals, doorstep deliveries and flexible van hire — tailored to your schedule.
            On time, every time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 flex flex-wrap items-center gap-3"
          >
            <a
              href="#quote"
              className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-6 py-4 inline-flex items-center gap-2"
              data-testid="hero-cta-book"
            >
              Book Your Move Today
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="tel:+447396764997"
              className="mono-cap text-[var(--jb-navy)] inline-flex items-center gap-2 border-b border-[var(--jb-navy)] pb-1"
              data-testid="hero-cta-call"
            >
              <Phone className="w-3.5 h-3.5" /> 07396 764997
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="md:col-span-3 flex md:justify-end"
          >
            <div className="mono-cap text-[var(--jb-navy)] leading-[2]">
              Fully Insured<br />
              Belfast-Based<br />
              Reliable & Affordable
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Spotlight Image */}
      <motion.div
        style={{ y: imgY }}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative mt-14 md:mt-20 max-w-[1440px] mx-auto px-6 md:px-10"
      >
        <div
          ref={imgRef}
          className="spotlight clip-tilt relative aspect-[16/8] w-full overflow-hidden"
          style={{ transition: "transform 0.6s ease-out", willChange: "transform" }}
        >
          <img
            src={HERO_IMAGE}
            alt="Jaybliss Logistics moving truck — professional removals across Belfast and the UK"
            fetchpriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="grain absolute inset-0" />
          <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 right-6 md:right-10 flex items-end justify-between text-[var(--jb-bone)] z-10">
            <div>
              <div className="mono-cap opacity-75">On time · Every time</div>
              <div className="serif text-2xl md:text-4xl mt-2">Chapter 01 — The Move</div>
            </div>
            <div className="hidden md:block mono-cap opacity-75">Scroll ↓</div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
